"use client";
import React, { useEffect, useState } from "react";
import { create, all } from "mathjs";
import Link from "next/link";

const math = create(all);

const CalculatorsDetails = ({
  calculatorData,
  inputsData,
  formulasData,
  relatedCalculators
}: {
  calculatorData: any;
  inputsData: any[];
  formulasData: any[];
  relatedCalculators: any[];
}) => {
  const [calculator, setCalculator] = useState<any>(null);
  const [inputs, setInputs] = useState<any[]>([]);
  const [formulas, setFormulas] = useState<any[]>([]);
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);


  useEffect(() => {
    if (calculatorData && inputsData && formulasData) {
      setCalculator(calculatorData);
      setInputs(inputsData);
      setFormulas(formulasData);
    }
  }, [calculatorData, inputsData, formulasData]);

  const updateInputUserValue = (value: string, index: number) => {
    const tempArray = [...inputs];
    tempArray[index].userInputValue = value;
    setInputs(tempArray);
    getResult();
  };

  const handleInputTypeChange = (e: any, index: number) => {
    const value = e.target.value as string;
    setInputs((prevInputs) => {
      const tempArray = [...prevInputs];
      tempArray[index].userInputValue = value;
      return tempArray;
    });
  };

  const getResult = () => {
    const calculatedResults = calculateResults(inputs, formulas);
    setResults(calculatedResults);
  };

  const calculateResults = (inputs: any[], formulas: any[]) => {
    const results: any[] = [];
    const context: { [key: string]: any } = {};
    formulas.forEach((formulaItem: any, index: number) => {
      // Replace input placeholders
      let formula: string = formulaItem.formula_value;
      formula = formula.replace(/@input(\d+)/g, (match: any, p1) => {
        const inputIndex = parseInt(p1) - 1;
        let value = "0";
        if (
          inputs[inputIndex]?.input_type === "number" &&
          inputs[inputIndex]?.input_value_type === "fixed"
        ) {
          value = inputs[inputIndex]?.fixedValue;
        }
        if (inputs[inputIndex]?.input_type === "dropdown") {
          value = `'${inputs[inputIndex]?.userInputValue}'`;
        } else {
          if (
            inputs[inputIndex]?.userInputValue === undefined ||
            inputs[inputIndex]?.userInputValue === ""
          ) {
            value = "0";
          } else {
            value = inputs[inputIndex]?.userInputValue;
          }
        }

        return value;
      });

      // Replace result placeholders
      formula = formula.replace(/@result(\d+)/g, (match, p1) => {
        const resultIndex = parseInt(p1) - 1;

        return results[resultIndex] ?? 0;
      });
      // Evaluate the formula
      try {
        const logicalEvaluatedFormula = evaluateLogicalExpression(formula);
        const result = math.evaluate(logicalEvaluatedFormula);
        results.push({
          result_title: formulaItem.formula_name,
          result: result,
        });
        context[`result${index + 1}`] = result;
      } catch (error) {
        console.error("Error evaluating formula:", formula, error);
        results.push(null);
      }
    });

    return results;
  };

  const evaluateLogicalExpression = (expression: string) => {
    const logicalRegex =
      /'([^']*)'\s*==\s*'([^']*)'\s*\?\s*(-?\d+(\.\d+)?)\s*:\s*(-?\d+(\.\d+)?)/g;
    return expression.replace(logicalRegex, (match, p1, p2, p3, p4, p5, p6) => {
      return p1 === p2 ? p3 : p5;
    });
  };

  return (
    <>
      {loading ? (
        <div className="flex items-center justify-center">
          {" "}
          <div
            className="w-12 h-12 rounded-full animate-spin
                    border-[3px] border-solid border-blue-500 border-t-transparent"
          ></div>
        </div>
      ) : (
        <>
          {calculator && (
            <div className="grid items-start grid-cols-1 gap-4 pt-2 lg:grid-cols-2">
              {/* Section 1 */}
              <div className="flex flex-col">
                {/* Top Banner Ad */}
                <div className="p-4 mb-4 text-center bg-gray-200 rounded-lg">
                  <p>Top Banner Ad</p>
                </div>
                {/* Calculator Section */}
                <div className="p-6 text-center bg-white border border-gray-300 rounded-lg shadow-lg">
                  <h2 className="mb-4 text-2xl font-bold text-black">
                    {calculator?.calculator_name}
                  </h2>
                  <div className="flex flex-col items-start">
                    {inputs.map((item: any, index: number) => {
                      if (item.input_type === "number") {
                        return (
                          <div key={index} className="w-full mb-4">
                            <label className="flex flex-col items-start pl-1 mb-2 text-sm font-bold text-gray-700">
                              {item.input_name}
                            </label>
                            <input
                              type="number"
                              value={
                                item.input_value_type === "fixed"
                                  ? item.fixedValue
                                  : item.userInputValue || ""
                              }
                              onChange={(e) =>
                                updateInputUserValue(e.target.value, index)
                              }
                              disabled={item.input_value_type === "fixed"}
                              placeholder={`Enter ${item.input_name}`}
                              className="w-full px-3 py-2 text-gray-700 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        );
                      } else if (item.input_type === "dropdown") {
                        return (
                          <div key={index} className="w-full mb-4">
                            <label className="flex flex-col items-start pl-1 mb-2 text-sm font-bold text-gray-700">
                              {item.input_name}
                            </label>
                            <select
                              value={item.userInputValue || ""}
                              onChange={(e) => handleInputTypeChange(e, index)}
                              className="w-full p-2 pl-3 mb-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                              <option value="">Select {item.input_name}</option>
                              {JSON.parse(item.input_options).map(
                                (itemSelect: any, indexSelect: number) => (
                                  <option key={indexSelect} value={itemSelect}>
                                    {itemSelect}
                                  </option>
                                )
                              )}
                            </select>
                          </div>
                        );
                      }
                      return null;
                    })}
                    <div className="mt-3 mb-4 text-sm text-left text-gray-500">
                      {results?.map((item: any, index: number) => (
                        <p key={index} className="mt-1 text-xl">
                          {item?.result_title}: <b>{item.result}</b>
                        </p>
                      ))}
                    </div>
                    <div className="mt-3 mb-4 text-sm text-left text-gray-500">
                      <p>{calculator?.calculator_description}</p>
                    </div>
                  </div>
                </div>
                {/* Bottom Banner Ad */}
                <div className="p-4 mt-4 text-center bg-gray-200 rounded-lg">
                  <p>Bottom Banner Ad</p>
                </div>
              </div>

              {/* Section 2 */}
              <div className="flex flex-col lg:flex-row">
                {/* Left Half - Ad */}
                <div className="flex-1 p-4 mb-4 text-center bg-gray-200 rounded-lg lg:mr-2 lg:mb-0">
                  <p>Advertisement</p>
                </div>
                {/* Right Half - Related Calculators */}
                <div className="flex-1 p-5 bg-white rounded-lg shadow-lg">
                  <h3 className="mb-4 text-xl font-bold text-gray-800">
                    Related Tools
                  </h3>
                  <ul className="pl-5 space-y-2 list-disc">
                    {relatedCalculators.slice(0, 8).map((calculator, index) => (
                      <li key={index} className="text-gray-600">
                        <Link
                          href={`/${calculator.calculator_slug}`}
                        >
                          {calculator.calculator_name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default CalculatorsDetails;
